/**
 * geometry.ts — helpers to author vector pictures as polygon cells.
 *
 * Each picture is built by carving its drawing into colored REGIONS, then
 * subdividing each region into N small cells (mosaic shards) so the
 * paint-by-numbers reveal is satisfyingly granular. Cells inherit the region's
 * colorId. The number of cells per color is steered to roughly match the
 * config's cells_per_color profile.
 */
import { VIEW, type PictureCell } from './types';
import { Rng } from '../util/rng';

export interface Region {
  colorId: number;
  poly: number[]; // flat polygon in picture-space
  weight?: number; // relative share of cells (default = area)
}

interface Pt {
  x: number;
  y: number;
}

function flatToPts(poly: number[]): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i < poly.length; i += 2) pts.push({ x: poly[i], y: poly[i + 1] });
  return pts;
}

function polyArea(pts: Pt[]): number {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    a += (pts[j].x + pts[i].x) * (pts[j].y - pts[i].y);
  }
  return Math.abs(a / 2);
}

function bounds(pts: Pt[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

function pointInPoly(px: number, py: number, pts: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x,
      yi = pts[i].y,
      xj = pts[j].x,
      yj = pts[j].y;
    const intersect =
      yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Shatter a region into shard cells by sampling jittered grid points inside the
 * polygon and emitting small diamond/quad shards around each. Visually reads as
 * a mosaic; geometrically each cell is a tiny convex quad clipped to be inside.
 */
function shardRegion(
  region: Region,
  count: number,
  rng: Rng,
  startId: number,
): PictureCell[] {
  const pts = flatToPts(region.poly);
  const b = bounds(pts);
  const cells: PictureCell[] = [];
  const w = Math.max(1, b.maxX - b.minX);
  const h = Math.max(1, b.maxY - b.minY);
  const area = polyArea(pts) || w * h;
  // Tile the region with a clean, NON-OVERLAPPING grid so the result reads as a
  // crisp mosaic (real paint-by-numbers), not a pile of scattered shards. Pick a
  // tile size that yields ~count interior tiles.
  let tile = Math.sqrt(area / Math.max(1, count));
  tile = Math.max(7, tile); // floor so tiles never go microscopic
  const cols = Math.max(1, Math.ceil(w / tile));
  const rows = Math.max(1, Math.ceil(h / tile));
  const stepX = w / cols;
  const stepY = h / rows;
  const grout = Math.min(stepX, stepY) * 0.13; // "grout" gap between tiles
  const jit = grout * 0.45; // organic jitter, strictly < grout so tiles never overlap
  const j = () => (rng.float() - 0.5) * jit;
  const clamp = (v: number) => Math.max(0, Math.min(VIEW, v));
  let id = startId;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x0 = b.minX + c * stepX;
      const y0 = b.minY + r * stepY;
      const cx = x0 + stepX / 2;
      const cy = y0 + stepY / 2;
      // a tile belongs to the region only if its centre is inside the shape
      if (!pointInPoly(cx, cy, pts)) continue;
      const lx0 = x0 + grout;
      const ly0 = y0 + grout;
      const lx1 = x0 + stepX - grout;
      const ly1 = y0 + stepY - grout;
      const poly = [
        clamp(lx0 + j()), clamp(ly0 + j()),
        clamp(lx1 + j()), clamp(ly0 + j()),
        clamp(lx1 + j()), clamp(ly1 + j()),
        clamp(lx0 + j()), clamp(ly1 + j()),
      ];
      cells.push({ cellId: id++, colorId: region.colorId, cx, cy, poly });
    }
  }
  return cells;
}

/**
 * Build all cells for a picture from its regions and a target total cell count.
 * Cells are distributed across regions proportional to (weight ?? area), then
 * lightly nudged toward the config's per-color profile if provided.
 */
export function buildCells(
  regions: Region[],
  totalCells: number,
  seed: string,
  perColorTarget?: Record<number, number>,
): PictureCell[] {
  const rng = new Rng(`cells:${seed}`);
  // group regions by color
  const colorIds = Array.from(new Set(regions.map((r) => r.colorId)));
  const areaByColor: Record<number, number> = {};
  for (const r of regions) {
    const a = r.weight ?? polyArea(flatToPts(r.poly));
    areaByColor[r.colorId] = (areaByColor[r.colorId] ?? 0) + a;
  }
  // desired cells per color
  const totalArea = Object.values(areaByColor).reduce((s, v) => s + v, 0) || 1;
  const cellsPerColor: Record<number, number> = {};
  let assigned = 0;
  for (const cid of colorIds) {
    const target =
      perColorTarget?.[cid] ??
      Math.max(6, Math.round((areaByColor[cid] / totalArea) * totalCells));
    cellsPerColor[cid] = target;
    assigned += target;
  }
  // scale to hit totalCells
  const scale = totalCells / Math.max(1, assigned);
  for (const cid of colorIds)
    cellsPerColor[cid] = Math.max(4, Math.round(cellsPerColor[cid] * scale));

  // distribute a color's cells across its regions by area
  const out: PictureCell[] = [];
  let nextId = 0;
  for (const cid of colorIds) {
    const colorRegions = regions.filter((r) => r.colorId === cid);
    const colArea =
      colorRegions.reduce((s, r) => s + (r.weight ?? polyArea(flatToPts(r.poly))), 0) ||
      1;
    let remaining = cellsPerColor[cid];
    colorRegions.forEach((r, idx) => {
      const a = r.weight ?? polyArea(flatToPts(r.poly));
      let n =
        idx === colorRegions.length - 1
          ? remaining
          : Math.max(1, Math.round((a / colArea) * cellsPerColor[cid]));
      n = Math.min(n, remaining);
      remaining -= n;
      const shards = shardRegion(r, n, rng, nextId);
      nextId += shards.length;
      out.push(...shards);
    });
  }
  return out;
}

/** Build a quad polygon from x,y,w,h. */
export function rect(x: number, y: number, w: number, h: number): number[] {
  return [x, y, x + w, y, x + w, y + h, x, y + h];
}

/** Regular-ish polygon helper. */
export function ngon(
  cx: number,
  cy: number,
  r: number,
  sides: number,
  rot = 0,
): number[] {
  const out: number[] = [];
  for (let i = 0; i < sides; i++) {
    const a = rot + (i / sides) * Math.PI * 2;
    out.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  return out;
}
